import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { HeartPulse, Activity, Stethoscope, FlaskConical, Brain, ScanLine, TestTube, RadiationIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Medical Triage — /cef/triage/:serverId — St. Auria's intake board.
   Vitals stream live on a monitor; every test you order costs stability while
   the patient's clock bleeds. Diagnose + treat before they crash. */

const TESTS = [
  ["fast_scan", "FAST scan", ScanLine],
  ["xray", "X-ray", RadiationIcon],
  ["bloods", "Blood panel", TestTube],
  ["ecg", "12-lead ECG", Activity],
  ["tox_screen", "Tox screen", FlaskConical],
  ["neuro", "Neuro exam", Brain],
];
const DIAGNOSES = [
  ["gsw", "Internal bleeding — penetrating trauma"],
  ["od", "Opioid-style overdose"],
  ["mi", "Cardiac event (STEMI)"],
  ["tbi", "Traumatic brain injury"],
  ["sepsis", "Septic shock"],
  ["asthma", "Status asthmaticus"],
];
const TREATMENTS = [
  ["transfusion", "Emergency transfusion"], ["surgery", "Crash laparotomy"],
  ["naloxone", "Counter-agent push"], ["airway", "Secure the airway"],
  ["thrombolytics", "Clot-busters"], ["cath_lab", "Straight to cath lab"],
  ["hyperventilate", "Controlled hyperventilation"], ["neurosurgery", "Emergency neurosurgery"],
  ["antibiotics", "Broad-spectrum antibiotics"], ["fluids", "Aggressive fluids"],
  ["nebulizer", "Back-to-back nebulizers"], ["magnesium", "IV magnesium"],
];

const DEMO_CASE = {
  id: "demo-case", patient: { name: "Bea Kestrel", age: 34, sex: "F" },
  vitals: { hr: 44, bp_sys: 88, spo2: 84, temp: 35.6 },
  symptoms: ["unresponsive", "pinpoint pupils", "shallow breathing 6/min"],
  clues: [], tests_done: [], stability: 82, status: "open", outcome: null,
};

export default function TriageCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [kase, setCase] = useState(null);
  const [dx, setDx] = useState("");
  const [tx, setTx] = useState("");
  const [busy, setBusy] = useState(false);
  const ecgRef = useRef(null);

  const nextCase = async () => {
    setBusy(true);
    try {
      if (demo) { setCase(JSON.parse(JSON.stringify(DEMO_CASE))); return; }
      const r = await api.post(`/nc/servers/${serverId}/triage/cases/next`, {}, { params: { token } });
      setCase(r.data.case);
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "No patient available"); }
    finally { setBusy(false); }
  };
  useEffect(() => { nextCase(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* decay demo stability + live-refresh real stability */
  useEffect(() => {
    if (!kase || kase.status !== "open") return;
    const t = setInterval(() => {
      setCase((c) => (c ? { ...c, stability: Math.max(0, c.stability - (demo ? 0.4 : 0.25)) } : c));
    }, 1500);
    return () => clearInterval(t);
  }, [kase?.id, kase?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ECG trace */
  useEffect(() => {
    const canvas = ecgRef.current;
    if (!canvas || !kase) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let x = 0;
    const frame = (now) => {
      const W = canvas.width = canvas.clientWidth;
      const H = canvas.height = canvas.clientHeight;
      ctx.fillStyle = "rgba(5,8,6,1)";
      ctx.fillRect(0, 0, W, H);
      const hr = kase.vitals?.hr ?? 70;
      const period = 60 / Math.max(20, hr); // seconds per beat
      const tt = now / 1000;
      ctx.strokeStyle = kase.stability < 30 ? "#f87171" : "#4ade80";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let i = 0; i < W; i++) {
        const ph = ((tt - (W - i) / 120) % period) / period;
        let y = 0;
        if (ph > 0.1 && ph < 0.16) y = -Math.sin((ph - 0.1) / 0.06 * Math.PI) * 0.55; // QRS spike
        else if (ph > 0.02 && ph < 0.08) y = Math.sin((ph - 0.02) / 0.06 * Math.PI) * 0.12; // P wave
        else if (ph > 0.25 && ph < 0.4) y = Math.sin((ph - 0.25) / 0.15 * Math.PI) * 0.18; // T wave
        ctx.lineTo(i, H * 0.55 + y * H * 0.8);
      }
      ctx.stroke();
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [kase?.id, kase?.vitals?.hr, kase?.stability]); // eslint-disable-line react-hooks/exhaustive-deps

  const runTest = async (test) => {
    if (busy) return;
    setBusy(true);
    try {
      if (demo) {
        const clue = { fast_scan: "FAST: abdomen clear — the problem isn't the belly (demo)", xray: "X-ray: no fractures, lungs clear (demo)", bloods: "Bloods: respiratory acidosis — CO2 retention (demo)", ecg: "ECG: sinus bradycardia, no ischaemia (demo)", tox_screen: "TOX: opioids off the chart — street analogue (demo)", neuro: "Neuro: unresponsive, pinpoint pupils (demo)" }[test];
        setCase((c) => ({ ...c, tests_done: [...c.tests_done, test], clues: [...c.clues, { test, label: test, result: clue }], stability: Math.max(0, c.stability - 4) }));
      } else {
        const r = await api.post(`/nc/servers/${serverId}/triage/cases/${kase.id}/test`, { token, test });
        setCase(r.data.case);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };

  const treat = async () => {
    if (!dx || !tx) { toast.error("Pick a diagnosis AND a treatment"); return; }
    setBusy(true);
    try {
      if (demo) {
        const ok = dx === "od" && (tx === "naloxone" || tx === "airway");
        setCase((c) => ({ ...c, status: ok ? "stabilized" : "open", stability: ok ? c.stability : Math.max(0, c.stability - 30), outcome: ok ? "Patient stabilized (demo)" : null }));
        toast[ok ? "success" : "error"](ok ? "STABILIZED (demo)" : "Wrong call — stability crashing (demo)");
      } else {
        const r = await api.post(`/nc/servers/${serverId}/triage/cases/${kase.id}/treat`, { token, diagnosis: dx, treatment: tx });
        setCase(r.data.case);
        toast[r.data.correct_diagnosis && r.data.correct_treatment ? "success" : "error"](r.data.message);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Treatment failed"); }
    finally { setBusy(false); }
  };

  const v = kase?.vitals || {};
  const stab = Math.round(kase?.stability ?? 0);

  return (
    <div data-testid="triage-page" className="min-h-screen bg-transparent flex items-center justify-center p-4 relative select-none">
      {demo && <PreviewRibbon what="triage board" />}
      {!kase ? (
        <button data-testid="triage-next" onClick={nextCase} disabled={busy}
          className="border border-red-500/40 bg-black/85 backdrop-blur-md px-8 py-6 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-red-300 hover:bg-red-500/10 transition-colors">
          <HeartPulse className="w-5 h-5 inline mr-2" /> Pull next patient
        </button>
      ) : (
        <div className="w-full max-w-3xl border border-white/12 bg-black/90 backdrop-blur-md" style={{ animation: "ncFadeUp 0.3s ease both" }}>
          <style>{`@keyframes ncFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }`}</style>
          {/* monitor header */}
          <div className="flex items-center gap-4 px-4 py-3 border-b border-white/10 flex-wrap">
            <HeartPulse className={`w-5 h-5 ${stab < 30 ? "text-red-400 animate-ping" : "text-red-400"}`} />
            <div>
              <p data-testid="triage-patient" className="font-sans font-bold uppercase tracking-wide text-sm text-zinc-100">{kase.patient.name} · {kase.patient.age}{kase.patient.sex}</p>
              <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em]">{kase.symptoms.join(" · ")}</p>
            </div>
            <div className="ml-auto flex items-center gap-4 font-mono text-[10px]">
              <span data-testid="vital-hr" className="text-emerald-400">HR {Math.round(v.hr ?? 0)}</span>
              <span data-testid="vital-bp" className="text-sky-300">BP {Math.round(v.bp_sys ?? 0)}/—</span>
              <span data-testid="vital-spo2" className="text-amber-300">SpO₂ {Math.round(v.spo2 ?? 0)}%</span>
              <span data-testid="vital-temp" className="text-zinc-400">{v.temp ?? "—"}°C</span>
            </div>
          </div>
          <canvas data-testid="triage-ecg" ref={ecgRef} className="w-full h-16 border-b border-white/10" />
          {/* stability */}
          <div className="px-4 py-2 border-b border-white/10">
            <div className="flex justify-between font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
              <span>Stability</span><span data-testid="triage-stability">{stab}%</span>
            </div>
            <div className="h-2 bg-white/5">
              <div className={`h-full transition-all duration-500 ${stab < 30 ? "bg-red-500" : stab < 60 ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${stab}%` }} />
            </div>
          </div>
          {kase.status !== "open" ? (
            <div data-testid="triage-outcome" className="p-6 text-center flex flex-col items-center gap-3">
              <p className={`font-sans font-bold uppercase tracking-[0.2em] text-lg ${kase.status === "stabilized" ? "text-emerald-400" : "text-red-400"}`}>
                {kase.status === "stabilized" ? "Patient stabilized" : "Patient lost"}
              </p>
              <p className="font-mono text-[10px] text-zinc-400">{kase.outcome}</p>
              <button data-testid="triage-next-after" onClick={nextCase} className="mt-2 bg-red-500 hover:bg-red-400 text-black font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-6 py-3 transition-colors">
                Next patient
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
              {/* tests */}
              <div className="p-4 border-r border-white/10 flex flex-col gap-1.5">
                <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Diagnostics (−4% stability each)</p>
                {TESTS.map(([id, label, Icon]) => (
                  <button key={id} data-testid={`test-${id}`} onClick={() => runTest(id)} disabled={busy || kase.tests_done.includes(id)}
                    className="flex items-center gap-2 border border-white/10 hover:border-sky-400/50 disabled:opacity-30 px-3 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-300 transition-colors">
                    <Icon className="w-3 h-3 text-sky-400" /> {label}
                  </button>
                ))}
              </div>
              {/* clues */}
              <div className="p-4 border-r border-white/10">
                <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Results</p>
                {kase.clues.length === 0 && <p className="font-mono text-[9px] text-zinc-600">No results yet — order tests, but watch the clock.</p>}
                {kase.clues.map((c, i) => (
                  <p key={i} data-testid={`clue-${i}`} className="font-mono text-[9px] text-sky-200/90 leading-relaxed border-l-2 border-sky-400/40 pl-2 mb-2">{c.result}</p>
                ))}
              </div>
              {/* decide */}
              <div className="p-4 flex flex-col gap-2">
                <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500">Diagnosis</p>
                <select data-testid="triage-dx" value={dx} onChange={(e) => setDx(e.target.value)}
                  className="bg-black border border-white/15 font-mono text-[10px] text-zinc-200 px-2 py-2 [color-scheme:dark]">
                  <option value="">— call it —</option>
                  {DIAGNOSES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
                <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Treatment</p>
                <select data-testid="triage-tx" value={tx} onChange={(e) => setTx(e.target.value)}
                  className="bg-black border border-white/15 font-mono text-[10px] text-zinc-200 px-2 py-2 [color-scheme:dark]">
                  <option value="">— play —</option>
                  {TREATMENTS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
                <button data-testid="triage-treat" onClick={treat} disabled={busy || !dx || !tx}
                  className="mt-2 bg-red-500 hover:bg-red-400 disabled:opacity-40 text-black font-mono text-[10px] font-bold uppercase tracking-[0.2em] py-3 flex items-center justify-center gap-2 transition-colors">
                  <Stethoscope className="w-3.5 h-3.5" /> Commit treatment
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
